import React, { useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { getDisplayName, isUserProfileReady, useAuth } from '../../contexts/AuthContext'
import LoadingSpinner from '../ui/LoadingSpinner'
import {
  Cog6ToothIcon,
  FolderOpenIcon,
  ArrowRightOnRectangleIcon,
  SparklesIcon,
  ArrowUpTrayIcon,
  ChartBarSquareIcon
} from '@heroicons/react/24/outline'
import { useLastMechanicalProjectId } from '../../mechanical-tests/useLastMechanicalProjectId'
import { useProjects } from '../../contexts/ProjectsContext'
import { WhatsNewModal } from './WhatsNewModal'
import { AppVersionText } from './AppVersionText'
import crcEvansLogo from '../../assets/images/crc-evans-logo.png'

interface NavItem {
  to: string
  label: string
  icon: React.ComponentType<{ className?: string }>
}

const mainNavItems: NavItem[] = [
  { to: '/resources', label: 'Project Hub', icon: FolderOpenIcon }
]

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  [
    'flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] font-medium transition-all duration-150',
    isActive
      ? 'bg-hero-red/15 text-white'
      : 'text-white/50 hover:text-white/80 hover:bg-white/5'
  ].join(' ')

export default function Sidebar() {
  const navigate = useNavigate()
  const [showWhatsNew, setShowWhatsNew] = useState(false)
  const { user, logout, userProfileLoading } = useAuth()
  const mechProjectId = useLastMechanicalProjectId()
  const { projects } = useProjects()
  // Pick a project for the mechanical-tests shortcuts: last visited, or the
  // first loaded project. If neither exists, the links route to Project Hub
  // so the user can create/pick a project.
  const targetProjectId = mechProjectId ?? projects[0]?.id ?? null
  const uploadTo = targetProjectId ? `/projects/${targetProjectId}/mechanical-tests/upload` : '/resources'
  const analyticsTo = targetProjectId ? `/projects/${targetProjectId}/mechanical-tests/analytics` : '/resources'
  const profileReady = !userProfileLoading && isUserProfileReady(user)
  const displayName = profileReady ? getDisplayName(user) : ''
  const initials = profileReady
    ? displayName
        .split(/[\s@._-]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((segment) => segment[0]?.toUpperCase())
        .join('')
    : ''

  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <aside
      className="hidden lg:flex lg:fixed lg:top-0 lg:left-0 lg:bottom-0 lg:flex-col bg-blueprint-blue z-50"
      style={{ width: 240 }}
    >
      <div className="h-[60px] flex items-center px-5 border-b border-white/10 gap-3 flex-shrink-0">
        <div
          className="h-9 w-9 rounded-xl bg-hero-red flex items-center justify-center flex-shrink-0 font-heading font-bold text-[17px] text-white leading-none"
          aria-hidden
        >
          W
        </div>
        <div className="min-w-0">
          <div className="font-heading font-semibold text-[14px] text-white leading-tight truncate">
            WPS Studio
          </div>
          <div className="text-[11px] text-white/45 truncate">Procedure Intelligence</div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-3 flex flex-col gap-1 overflow-y-auto">
        {mainNavItems.map((item) => (
          <NavLink key={item.to} to={item.to} className={navLinkClass}>
            <item.icon className="h-[18px] w-[18px]" />
            <span>{item.label}</span>
          </NavLink>
        ))}

        <NavLink to={uploadTo} className={navLinkClass}>
          <ArrowUpTrayIcon className="h-[18px] w-[18px]" />
          <span>Upload</span>
        </NavLink>
        <NavLink to={analyticsTo} className={navLinkClass}>
          <ChartBarSquareIcon className="h-[18px] w-[18px]" />
          <span>Analytics</span>
        </NavLink>
      </nav>  

      <div className="px-3 pb-3">
          <NavLink to="/settings" className={navLinkClass}>
            <Cog6ToothIcon className="h-[18px] w-[18px]" />
            <span>Settings</span>
          </NavLink>
        </div>

      <div className="border-t border-white/10 flex-shrink-0">
        

        <div className="px-3 py-3">
          <div className="flex items-center gap-3 px-2 py-2 rounded-lg">
            <div className="w-8 h-8 rounded-full bg-hero-red flex items-center justify-center flex-shrink-0">
              {profileReady ? (
                <span className="text-[12px] font-semibold text-white font-heading">{initials}</span>
              ) : (
                <LoadingSpinner
                  size="sm"
                  className="border-white/60 border-r-transparent"
                />
              )}
            </div>
            <div className="flex-1 min-w-0">
              {profileReady ? (
                <>
                  <div className="text-[13px] font-medium text-white truncate">{displayName}</div>
                  <div className="text-[11px] text-white/40 truncate">{user?.email}</div>
                </>
              ) : (
                <div
                  className="flex items-center py-1"
                  data-testid="sidebar-user-profile-loading"
                  aria-label="Loading user profile"
                >
                  <LoadingSpinner
                    size="sm"
                    className="border-white/60 border-r-transparent"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-3 pb-2">
          <button
            type="button"
            onClick={() => setShowWhatsNew(true)}
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-white/75 hover:text-white hover:bg-white/5 transition-all duration-150 w-full text-[13px] font-medium border border-white/15 mb-2"
          >
            <SparklesIcon className="h-4 w-4" />
            <span>What&apos;s New</span>
          </button>

          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-hero-red-30 hover:bg-white/5 transition-all duration-150 w-full text-[13px] font-medium"
          >
            <ArrowRightOnRectangleIcon className="h-4 w-4" />
            <span>Logout</span>
          </button>
        </div>

        <div className="px-4 py-3 border-t border-white/10">
          <AppVersionText className="text-[10px] text-white/25 text-center mb-5" />
          <div className="flex items-center justify-center mb-1 text-[10px] text-white/25 text-center"> Powered by </div>
          <a
            href="https://www.crcevans.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center mb-3 opacity-80 hover:opacity-100 transition-opacity"
          >
            <img src={crcEvansLogo} alt="CRC Evans" className="h-7 w-auto object-contain" />
          </a>
        </div>
      </div>
      <WhatsNewModal open={showWhatsNew} onClose={() => setShowWhatsNew(false)} />
    </aside>
  )
}
